/**
 * @fileoverview Profile Service - Handles user profile management operations
 * @active Used for managing user profiles in the app.app_user table
 *
 * This service provides a centralized interface for all profile-related operations.
 * It uses direct Supabase database operations for profile management and Supabase
 * Auth for authentication-related profile updates.
 *
 * @see {@link src/app/utils/supabase/client.ts} - Supabase client utilities
 * @see {@link src/app/utils/supabase/server.ts} - Server-side Supabase operations
 * @see {@link src/types/userAuth.ts} - User and profile type definitions
 * @see {@link src/lib/db.server.ts} - Direct database connection utilities
 * @see {@link src/utils/avatarStorage.ts} - Avatar storage utilities
 * @see {@link src/utils/authHelpers.ts} - Auth helper utilities
 */

"use client";

import { createClientComponent } from "@/app/utils/supabase/client";
import { aventureApi } from "@/services/apiService/aventureApi";
import { axiosService } from "./apiService/axiosService";
import { loadingError } from "./error.service";
import { profileActions } from "@/store/profile/profile.slice";
import avatarStorage from "@/utils/avatarStorage";
import { getCurrentUser } from "@/utils/authHelpers";
import {
  type UserProfile,
  type UserProfileUpdate,
  type CustomQueryReturnValue,
  type customErrorObject,
  createErrorObject,
  handleRTKError,
  dispatchSuccess,
  normalizeSocialLinksForDB,
} from "@/types/userAuth";

// Server actions - import server-side database operations
import {
  getUserProfileAction,
  getUserBillingAction,
  getUserSubscriptionAction,
  updateUserInfoAction,
  updateSocialLinkAction,
  disconnectSocialAction,
  updateUserProfileAction,
} from "@/services/profile.server";

/**
 * Helper function to display success messages consistently
 * @param dispatch - Redux dispatch function
 * @param message - Success message to display
 * @param isBlurPopup - Whether to skip message display for popup operations
 */
const showSuccessMessage = (
  dispatch: Function,
  message: string,
  isBlurPopup?: boolean
) => {
  if (!isBlurPopup) {
    dispatch(loadingError({ message }));
  }
};

// TODO: This file still references old backend API endpoints -- they should be updated to use Supabase direct queries

interface BillingResponse {
  url: string;
}

interface SubscriptionResponse {
  url: string;
}

const profileApi = aventureApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserProfile: builder.query({
      /**
       * Retrieves user profile information directly from Supabase
       *
       * @param {any} _ - Unused parameter
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: UserProfile} | {error: any}>} User profile or error
       */
      async queryFn(_, { dispatch }) {
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          const userProfile = await getUserProfileAction(user.id);
          if (!userProfile) {
            throw new Error("User profile not found");
          }

          dispatch(profileActions.setProfile(userProfile));
          return { data: userProfile };
        } catch (error: unknown) {
          return { error: handleRTKError(error) };
        }
      },
      providesTags: ["profile"],
    }),

    getUserBilling: builder.query<BillingResponse[], void>({
      /**
       * Retrieves user billing information directly from the database
       *
       * NOTE: Currently implemented with direct SQL but the target table
       * `app.app_user_payment` is missing from the schema. This implementation
       * will likely fail in production if the table doesn't exist.
       *
       * @param {any} _ - Unused parameter
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Billing data or error
       */
      async queryFn(_, { dispatch }) {
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          const billingData = await getUserBillingAction(user.id);
          return { data: [{ url: billingData[0]?.url }] };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      providesTags: () => [{ type: "profile" }],
    }),

    getUserSubscription: builder.query<
      SubscriptionResponse,
      { priceId: string; successUrl: string }
    >({
      /**
       * Retrieves user subscription information directly from the database
       *
       * NOTE: Currently implemented with direct SQL but the target table
       * `app.app_user_subscription` is missing from the schema. This implementation
       * will likely fail in production if the table doesn't exist.
       *
       * @param {any} params - Query parameters
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Subscription data or error
       */
      async queryFn({ priceId, successUrl }, { dispatch }) {
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          const subscriptionData = await getUserSubscriptionAction(
            user.id,
            priceId
          );
          return { data: { url: subscriptionData[0]?.url } };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      providesTags: () => [{ type: "profile" }],
    }),

    updateUserInfo: builder.mutation({
      /**
       * Updates user personal information
       *
       * @param {Object} options - User information to update
       * @param {boolean} [options.isBlurPopup] - Whether to blur popup (UI state)
       * @param {string} [options.userFirstName] - User's first name
       * @param {string} [options.userLastName] - User's last name
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Success or error response
       */
      async queryFn(options, { dispatch }) {
        const { isBlurPopup, ...rest } = options;
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          const updateData: UserProfileUpdate = {};
          if (rest.userFirstName)
            updateData.user_first_name_id = rest.userFirstName;
          if (rest.userLastName)
            updateData.user_last_name_id = rest.userLastName;
          if (rest.userFirstName && rest.userLastName) {
            updateData.user_full_name_id = `${rest.userFirstName} ${rest.userLastName}`;
          }

          // Add additional fields if present in the rest object
          if (rest.userLinkedinId)
            updateData.user_linkedin_id = rest.userLinkedinId;
          if (rest.userTwitterId)
            updateData.user_twitter_id = rest.userTwitterId;
          if (rest.userWebsiteId)
            updateData.user_website_id = rest.userWebsiteId;

          // Update in Supabase Auth metadata first
          const { error: metadataError } =
            await createClientComponent().auth.updateUser({
              data: {
                userFirstName:
                  typeof rest.userFirstName === "string"
                    ? rest.userFirstName
                    : "",
                userLastName:
                  typeof rest.userLastName === "string"
                    ? rest.userLastName
                    : "",
                name:
                  typeof rest.userFirstName === "string" &&
                  typeof rest.userLastName === "string"
                    ? `${rest.userFirstName} ${rest.userLastName}`
                    : "",
              },
            });

          if (metadataError) {
            console.error(
              "Error updating Supabase Auth metadata:",
              metadataError
            );
          }

          const updateResult = await updateUserInfoAction(user.id, updateData);

          if (!updateResult) {
            throw new Error("Failed to update user profile");
          }

          showSuccessMessage(
            dispatch,
            "Profile was saved successfully!",
            isBlurPopup
          );

          return { data: updateResult };
        } catch (error) {
          console.error("Error updating user info:", error);
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    changePassword: builder.mutation({
      /**
       * Changes user password
       *
       * @param {Object} options - Password change parameters
       * @param {string} options.oldPassword - Current password
       * @param {string} options.newPassword - New password
       * @param {boolean} [options.isBlurPopup] - Whether to blur popup (UI state)
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Success or error response
       */
      async queryFn(options, { dispatch }) {
        const { isBlurPopup, oldPassword, newPassword } = options;
        try {
          // Use Supabase Auth to update password (requires current password)
          const supabase = createClientComponent();

          // First verify the current password is correct
          const { error: signInError } = await supabase.auth.signInWithPassword(
            {
              email: "", // We'll automatically use the current user's email
              password: oldPassword,
            }
          );

          if (signInError) {
            // Handle different error types
            if (signInError.message.includes("Invalid login credentials")) {
              throw new Error("Current password is incorrect");
            } else if (signInError.status === 429) {
              throw new Error("Too many attempts. Please try again later");
            }
            throw signInError;
          }

          // Change password
          const { error: updateError } = await supabase.auth.updateUser({
            password: newPassword,
          });

          if (updateError) {
            throw updateError;
          }

          showSuccessMessage(
            dispatch,
            "Password changed successfully!",
            isBlurPopup
          );

          return { data: { success: true } };
        } catch (error) {
          console.error("Error changing password:", error);
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
    }),

    updateSocialLink: builder.mutation({
      /**
       * Updates user's social media links
       *
       * @param {Object} options - Social media links using either snake_case DB fields or camelCase UI fields
       * @param {string} [options.userLinkedinId] - LinkedIn profile URL (snake_case DB field)
       * @param {string} [options.userTwitterId] - Twitter/X profile URL (snake_case DB field)
       * @param {string} [options.userWebsiteId] - Personal website URL (snake_case DB field)
       * @param {string} [options.linkedin] - LinkedIn profile URL (camelCase UI field)
       * @param {string} [options.twitter] - Twitter/X profile URL (camelCase UI field)
       * @param {string} [options.website] - Personal website URL (camelCase UI field)
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Success or error response
       * @see {@link /src/types/userAuth.ts#SocialLinks} - Standardized social links interface
       */
      async queryFn(options, { dispatch }) {
        try {
          // Get current user from Supabase Auth
          const supabase = createClientComponent();
          const { data: authData, error: authError } =
            await supabase.auth.getUser();

          if (authError || !authData.user) {
            throw new Error(authError?.message || "User not authenticated");
          }

          // Normalize social links using utility function from userAuth.ts
          const normalized = normalizeSocialLinksForDB(options);

          // Create a database update object with snake_case keys
          // Access the correct snake_case properties from the normalized object
          const updateData: UserProfileUpdate = {
            user_linkedin_id: normalized.user_linkedin_id,
            user_twitter_id: normalized.user_twitter_id,
            user_website_id: normalized.user_website_id,
          };

          const updateResult = await updateSocialLinkAction(
            authData.user.id,
            updateData
          );

          if (!updateResult) {
            throw new Error("Failed to update social profiles");
          }

          showSuccessMessage(
            dispatch,
            "Social profiles are successfully saved!"
          );

          return { data: updateResult };
        } catch (error) {
          console.error("Error updating social links:", error);
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    addAvatar: builder.mutation({
      /**
       * Uploads user avatar to Supabase Storage
       * Uses the centralized avatarStorage utility
       *
       * @deprecated Use updateUserAvatar instead for consistency
       * @param {File} file - Image file to upload
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Success or error response
       * @see {@link src/utils/avatarStorage.ts} - Avatar storage implementation
       * @see {@link updateUserAvatar} - Preferred method for uploading avatars
       */
      async queryFn(file, { dispatch }) {
        try {
          const result = await avatarStorage.uploadAvatar(file);

          showSuccessMessage(dispatch, "Avatar changed successfully!");

          return { data: result };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    deleteAvatar: builder.mutation({
      /**
       * Deletes user avatar from Supabase Storage
       * Uses the centralized avatarStorage utility
       *
       * @param {void} _ - No parameters needed
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Success or error response
       * @see {@link src/utils/avatarStorage.ts} - Avatar storage implementation
       */
      async queryFn(_, { dispatch }) {
        try {
          const result = await avatarStorage.deleteAvatar();

          showSuccessMessage(dispatch, "Avatar deleted successfully!");

          return { data: result };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    disconnectSocial: builder.mutation({
      /**
       * Disconnects a social media provider from a user's account
       * Handles both OAuth providers (google, linkedin) and profile links (twitter, website)
       *
       * @param {string} provider - Social provider to disconnect (e.g., "google", "linkedin", "twitter", "website")
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<CustomQueryReturnValue>} Success or error response
       * @see {@link src/app/utils/supabase/client.ts} - Supabase client utilities
       * @see {@link src/types/userAuth.ts} - Provider type definitions
       */
      async queryFn(provider, { dispatch }) {
        try {
          // Get current user from Supabase Auth
          const supabase = createClientComponent();
          const { data: authData, error: authError } =
            await supabase.auth.getUser();

          if (authError || !authData.user) {
            throw new Error(authError?.message || "User not authenticated");
          }

          // Map of provider names to database column names
          const providerColumnMap = {
            google: "user_social_login_google_uid",
            linkedin: "user_social_login_linkedin_uid",
            twitter: "user_twitter_id",
            website: "user_website_id",
          };

          // Get the correct column name based on provider
          const columnName =
            providerColumnMap[provider as keyof typeof providerColumnMap];

          if (!columnName) {
            throw new Error(`Unsupported provider: ${provider}`);
          }

          const result = await disconnectSocialAction(
            authData.user.id,
            columnName
          );

          showSuccessMessage(dispatch, "Disconnected successfully!");

          return {
            data: {
              success: true,
              message: "Social account disconnected successfully",
              provider,
            },
          };
        } catch (error) {
          console.error("Error disconnecting social account:", error);
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    userReverseSupport: builder.mutation({
      /**
       * @deprecated Use direct Supabase queries instead
       * Submits user support request directly to the database
       *
       * NOTE: Currently implemented with direct SQL but the target table
       * `app.app_user_support` is missing from the schema. This implementation
       * will fail in production if the table doesn't exist.
       *
       * @param {Object} options - Support request data
       * @param {string} options.message - Support message content
       * @param {string} options.subject - Support request subject
       * @param {string} [options.category] - Support request category
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: any} | {error: any}>} Support request data or error
       */
      async queryFn(options, { dispatch }) {
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          // Validate required fields
          if (!options.message) throw new Error("Support message is required");
          if (!options.subject) throw new Error("Support subject is required");

          // DEPRECATED: This functionality uses direct SQL which is not available in client components
          // and the target table may not exist. Commented out to prevent runtime errors.
          /*
          const result = await sql`
            INSERT INTO app.app_user_support (
              user_id,
              message,
              subject,
              category,
              created_at,
              status
            ) VALUES (
              ${user.id},
              ${options.message},
              ${options.subject},
              ${options.category || "General"},
              ${new Date().toISOString()},
              'pending'
            )
            RETURNING *
          `;
          */

          // Since this is deprecated, provide a fallback error
          throw new Error(
            "This feature has been deprecated. Please use the contact form instead."
          );

          // showSuccessMessage(
          //   dispatch,
          //   "Support request submitted successfully!"
          // );

          // return { data: result[0] };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
    }),

    investmentInAccFundStripe: builder.mutation({
      /**
       * @deprecated Use direct Supabase queries instead
       * Processes investment payment
       *
       * TODO: MIGRATION BLOCKED - Target table unclear and this requires Stripe API integration.
       * Further investigation needed to determine the correct approach for handling
       * investment payments with Supabase and Stripe.
       */
      async queryFn(price, { dispatch }) {
        try {
          const { data } = await axiosService.post(
            `app/user/investment-payment`,
            {
              price,
            }
          );

          return { data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    generateQrCode: builder.query({
      /**
       * @deprecated Use Supabase Auth MFA APIs instead
       * Generates 2FA QR code
       *
       * TODO: MIGRATION BLOCKED - This requires implementation using Supabase Auth's
       * Multi-Factor Authentication (MFA) APIs. The current axiosService implementation
       * should be replaced with calls to Supabase Auth's MFA enrollment APIs.
       *
       * @see {@link https://supabase.com/docs/reference/javascript/auth-mfa-enroll} - Supabase MFA enrollment
       */
      async queryFn(_, { dispatch }) {
        try {
          const { data } = await axiosService.get(`app/auth/qr`);

          return { data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
    }),

    toggle2faCode: builder.mutation({
      /**
       * @deprecated Use Supabase Auth MFA APIs instead
       * Toggles 2FA authentication
       *
       * TODO: MIGRATION BLOCKED - This requires implementation using Supabase Auth's
       * Multi-Factor Authentication (MFA) APIs. The current axiosService implementation
       * should be replaced with calls to Supabase Auth's MFA verification APIs.
       *
       * @see {@link https://supabase.com/docs/reference/javascript/auth-mfa-challengeverify} - Supabase MFA verification
       */
      async queryFn(authCode) {
        try {
          const { data } = await axiosService.post(
            "/app/auth/turn-on-2fa",
            authCode
          );

          return { data };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: [{ type: "profile" }],
    }),

    getPricing: builder.query({
      /**
       * @deprecated Use direct Supabase queries instead
       * Retrieves pricing information
       *
       * TODO: MIGRATION BLOCKED - Target table unclear, likely `app.app_pricing` or similar
       * but not found in schema. Need to verify the correct table name and structure
       * before implementing with direct Postgres queries.
       */
      async queryFn(params, { dispatch }) {
        try {
          const { data } = await axiosService.get("app/payment/prices", {
            params: { ...params },
          });

          return { data: data?.data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
    }),

    updateUserProfile: builder.mutation({
      /**
       * Updates user profile information in both Supabase Auth and database
       *
       * @param {UserProfileUpdate} updates - Profile fields to update
       * @returns {Promise<CustomQueryReturnValue<UserProfile>>} Updated profile or error
       */
      async queryFn(updates: UserProfileUpdate, { dispatch }) {
        try {
          const user = await getCurrentUser();
          if (!user) throw new Error("User not authenticated");

          // Update Supabase Auth metadata
          const { error: authError } =
            await createClientComponent().auth.updateUser({
              data: {
                first_name: updates.user_first_name_id,
                last_name: updates.user_last_name_id,
                full_name: updates.user_full_name_id,
              },
            });

          if (authError) {
            throw authError;
          }

          const result = await updateUserProfileAction(user.id, updates);

          if (!result) {
            throw new Error("Failed to update profile");
          }

          showSuccessMessage(dispatch, "Profile updated successfully");
          return { data: result };
        } catch (error: unknown) {
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: ["profile"],
    }),

    updateUserAvatar: builder.mutation({
      /**
       * Updates user avatar in Supabase Storage and database
       * Uses the centralized avatarStorage utility
       * This is the preferred method for avatar management.
       *
       * @param {File} file - Image file to upload
       * @param {Object} param1 - Object containing dispatch function
       * @returns {Promise<{data: UserProfile} | {error: any}>} Success or error response with updated profile
       * @see {@link src/utils/avatarStorage.ts} - Avatar storage implementation
       */
      async queryFn(file: File, { dispatch }) {
        try {
          const result = await avatarStorage.uploadAvatar(file);

          showSuccessMessage(dispatch, "Avatar updated successfully");
          return { data: result.profile };
        } catch (error: unknown) {
          dispatch(loadingError(error));
          return { error: handleRTKError(error) };
        }
      },
      invalidatesTags: ["profile"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetUserProfileQuery,
  useLazyGetUserBillingQuery,
  useLazyGetUserSubscriptionQuery,
  useUpdateUserInfoMutation,
  useChangePasswordMutation,
  useUpdateSocialLinkMutation,
  useAddAvatarMutation,
  useDeleteAvatarMutation,
  useDisconnectSocialMutation,
  useUserReverseSupportMutation,
  useInvestmentInAccFundStripeMutation,
  useGenerateQrCodeQuery,
  useToggle2faCodeMutation,
  useLazyGetPricingQuery,
  useUpdateUserProfileMutation,
  useUpdateUserAvatarMutation,
} = profileApi;

export default profileApi;
